/**
 * Sentry - مراقبة الأخطاء وتتبعها
 * يُستورد أولاً في main.tsx لالتقاط أي خطأ قبل تشغيل التطبيق
 * @see https://sentry.io/
 */
import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN

function flushPendingInlineErrorsToSentry() {
  if (typeof window === 'undefined') return
  const q = window.__ehPendingErrors
  if (!q?.length) return
  const batch = q.splice(0, q.length)
  for (const item of batch) {
    Sentry.captureMessage(`Early inline script: ${item.source}`, {
      level: 'warning',
      extra: {
        error: item.error,
        name: item.name,
        queuedAt: item.time,
      },
    })
  }
}

if (dsn && typeof window !== 'undefined') {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: __SENTRY_RELEASE__,
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    ignoreErrors: [
      /ResizeObserver loop/,
      /Non-Error promise rejection/,
      /index\.js\.map/,
      /Cross-Origin-Resource-Policy/,
      /sdk\.tap\.company.*\.map/,
      // Snapchat Pixel SDK - خطأ في WebView (iOS) لا يدعم named capture groups في regex
      /Invalid regular expression: invalid group specifier name/,
      // Yandex Metrika — أخطاء من سكريبتات الطرف الثالث أو ad blockers
      /mc\.yandex/,
      /yandex\.ru/,
      /ym is not defined/,
      /ym is not a function/,
      // Apple Pay SDK — أخطاء داخلية من Tap SDK
      /InvalidAccessError/,
      /InvalidStateError/,
      /InvalidAbsoluteError/,
      /active payment session/,
      /completePayment/,
      /validatemerchant/,
      /mw-sdk\.tap/,
    ],
    beforeSend(event) {
      // تجاهل الأخطاء القادمة من سكريبتات الطرف الثالث (Yandex, Snap, TikTok)
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const isThirdParty = frames.some(f =>
        f.filename && (
          f.filename.includes('mc.yandex.ru') ||
          f.filename.includes('yandex.ru') ||
          f.filename.includes('sc-static.net') ||
          f.filename.includes('analytics.tiktok.com') ||
          f.filename.includes('mw-sdk.tap.company')
        )
      );
      if (isThirdParty) return null;
      return event;
    },
  })

  flushPendingInlineErrorsToSentry()

  // Global handler للأخطاء غير المُعالجة من سكريبتات الطرف الثالث
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg: string = reason?.message || reason?.name || String(reason || '');

    const isAnalyticsNoise =
      msg.includes('mc.yandex') ||
      msg.includes('yandex.ru') ||
      msg.includes('sc-static.net') ||
      msg.includes('analytics.tiktok') ||
      msg.includes('function Error() { [native code] }');

    if (isAnalyticsNoise) {
      event.preventDefault(); // يمنع Sentry من التقاطه
    }
  });
}
